import { Events, GridApi } from '@ag-grid-community/core'
import { RootState, selectActiveSession } from '@helios/state-management'
import { Store } from '@ngrx/store'
import { fromEvent, merge, Observable } from 'rxjs'
import { debounceTime, filter, map, takeUntil } from 'rxjs/operators'
import {
  isCommentsFilterActive,
  rowHasComments,
} from '../columns/get-commentary-column'
import { liveThreadChanges$ } from '../live/live-thread-changes'
import { CommentaryCountsService } from '../services/commentary-counts.service'
import { CommentaryCountDimension } from '../types/commentary-count-dimension'
import { CommentaryRowContext } from '../types/commentary-row-context'
import { readCommentaryShadowEvent } from '../types/cross-shadow-events'

// The backend answers the filter from an eventually consistent index, so a
// reload straight after the change can miss it.
export const THREAD_CHANGE_SETTLE_MS = 1000

type AccountId = string | number | null | undefined

/**
 * DLITE-5094: which accounts' live changes can move a server-side grid's
 * rows. `liveThreadChanges$` carries every displayed account in the tab (the
 * session, the drawer and every loaded row of every account list), so without
 * this each Comments-filtered grid would re-query for changes it can't contain.
 *
 * Known limit: a grid without `queriedAccountIds` (the account lists) can't
 * see accounts it hasn't loaded, so a live change can't bring in an account
 * that is currently filtered out, even if the session, the drawer or another
 * grid subscribes it. Before DLITE-5094 only the user's own drawer edits did
 * that, and they still do. Covering every possible entrant would need the
 * list's full query membership, which the client doesn't have.
 */
export interface LiveRefilterScope<TRow> {
  /** The account of a loaded row; the grid's loaded accounts are in scope. */
  accountIdOf: (row: TRow | undefined) => AccountId
  /**
   * Accounts the grid queries beyond its loaded rows — e.g. the session
   * accounts an entity grid reads — so a row can still enter the results when
   * none of that account's rows currently match the filter.
   */
  queriedAccountIds?: () => Iterable<AccountId>
}

/** Whether any of `changed` is a queried or loaded account of the grid. */
function touchesScope<TRow>(
  api: GridApi,
  scope: LiveRefilterScope<TRow>,
  changed: string[],
): boolean {
  const ids = new Set(changed)
  for (const id of scope.queriedAccountIds?.() ?? []) {
    if (id != null && ids.has(String(id))) return true
  }
  let touched = false
  api.forEachNode(node => {
    if (touched) return
    const id = scope.accountIdOf(node.data as TRow | undefined)
    if (id != null && ids.has(String(id))) touched = true
  })
  return touched
}

/**
 * DLITE-5094: the active session's loaded accounts, kept current until
 * `destroy$` — the `queriedAccountIds` of an entity grid, which reads the
 * session's accounts.
 */
export function activeSessionAccountIds(
  store: Store<RootState>,
  destroy$: Observable<void>,
): () => string[] {
  let ids: string[] = []
  store
    .select(selectActiveSession)
    .pipe(
      map(session =>
        (session?.loadedAccountIds ?? [])
          .filter(id => id != null)
          .map(id => String(id)),
      ),
      takeUntil(destroy$),
    )
    .subscribe(next => (ids = next))
  return () => ids
}

/**
 * For server-side grids: reloads the rows when a thread changes while the
 * comments filter is on, so a row that gained or lost its last open thread
 * moves in or out. A change is either the drawer's in-app
 * `commentary-threads-changed` event or (DLITE-5094) a live invalidation from
 * the thread-event socket — a change made anywhere else, or a reconnect that
 * may have missed some — for an account in the grid's `liveScope`. Both wait
 * for the same settle window. Out-of-scope live changes are dropped before
 * the settle window, so they neither reload the grid nor hold back a reload
 * for its own accounts.
 */
export function refilterOnThreadChanges<TRow>(
  getApi: () => GridApi,
  destroy$: Observable<void>,
  liveScope: LiveRefilterScope<TRow>,
): void {
  const drawerChanges$ = fromEvent(window, 'commentary-threads-changed').pipe(
    filter(
      event =>
        readCommentaryShadowEvent('commentary-threads-changed', event) != null,
    ),
  )
  const scopedLiveChanges$ = liveThreadChanges$.pipe(
    // Filter-off first: it is cheap, and walking the rows is not.
    filter(
      ids =>
        isCommentsFilterActive(getApi()) &&
        touchesScope(getApi(), liveScope, ids),
    ),
  )
  merge(drawerChanges$, scopedLiveChanges$)
    .pipe(
      debounceTime(THREAD_CHANGE_SETTLE_MS),
      filter(() => isCommentsFilterActive(getApi())),
      takeUntil(destroy$),
    )
    .subscribe(() => getApi().onFilterChanged())
}

export interface CommentCountRefilter<TRow> {
  getApi: () => GridApi
  destroy$: Observable<void>
  countsService: CommentaryCountsService
  commentary: CommentaryRowContext<TRow>
  countDimension: CommentaryCountDimension
}

/**
 * For client-side grids, which filter on the loaded counts: re-runs the
 * filter when a count update changes which rows have comments.
 *
 * Only on a change, because the re-run fires modelUpdated, which loads
 * counts, and a failed load emits again; refiltering on every emission
 * would retry the failing load in a loop.
 */
export function refilterOnCommentCountChanges<TRow>(
  w: CommentCountRefilter<TRow>,
): void {
  const commentedRows = (): string => {
    const ids: string[] = []
    w.getApi().forEachNode(node => {
      if (
        node.data &&
        // DLITE-5094: same availability-aware classification as the column's
        // filterValueGetter, so a denied/recovered account re-runs the filter.
        rowHasComments(
          w.countsService,
          w.commentary.build(node.data as TRow),
          w.countDimension,
        )
      ) {
        ids.push(String(node.id))
      }
    })
    return ids.join('\n')
  }

  let filteredOn: string | null = null
  const onFilterChanged = () => {
    filteredOn = isCommentsFilterActive(w.getApi()) ? commentedRows() : null
  }
  const api = w.getApi()
  api.addEventListener(Events.EVENT_FILTER_CHANGED, onFilterChanged)

  w.countsService.changes$
    .pipe(
      filter(() => isCommentsFilterActive(w.getApi())),
      takeUntil(w.destroy$),
    )
    .subscribe({
      next: () => {
        if (commentedRows() !== filteredOn) w.getApi().onFilterChanged()
      },
      complete: () =>
        api.removeEventListener(Events.EVENT_FILTER_CHANGED, onFilterChanged),
    })
}
